import {FC, HTMLAttributes, ReactNode, forwardRef} from 'react'
import {
    StyledAnswerFieldTitle,
    StyledContent,
    StyledFieldContent,
    StyledNoneValue,
    StyledSectionTitle,
    StyledTitle
} from './style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from '@/translations/i18n'
import {CheckIcon, XCloseIcon} from '@/components/ui/icon/Icon'
import {palette} from '@/theme/palette'

export const Title = (props: HTMLAttributes<HTMLHeadElement>) => <StyledTitle {...props} />

export const SectionTitle = (props: HTMLAttributes<HTMLParagraphElement>) => <StyledSectionTitle {...props} />

export const NoneValue = (props: HTMLAttributes<HTMLParagraphElement>) => <StyledNoneValue {...props} />

type ContentProps = HTMLAttributes<HTMLDivElement> & {
    gap?: number
    title?: string
    sectionTitle?: string
    columns?: number
    margin?: number
}
export const Content = forwardRef<HTMLDivElement, ContentProps>((props, ref) => {
    const {children, gap = 4, margin = 4, title, sectionTitle, columns = 4, ...rest} = props
    return (
        <StyledContent $gap={gap} $columns={columns} $margin={margin} {...rest} ref={ref}>
            {title && <Title>{title}</Title>}
            {sectionTitle && <SectionTitle>{sectionTitle}</SectionTitle>}
            <div>{children}</div>
        </StyledContent>
    )
})

interface AnswerFieldProps {
    labelTitle: ReactNode
    value?: string | number
    hasValue?: boolean
    children?: ReactNode
}
export const AnswerField: FC<AnswerFieldProps> = ({labelTitle, value, children, hasValue = true}) => {
    const {t} = useTranslation()
    return (
        <Flexbox direction={'column'} gap={1.5} fullWidth>
            {labelTitle && <StyledAnswerFieldTitle>{labelTitle}</StyledAnswerFieldTitle>}

            {hasValue ? (
                <StyledFieldContent>{children ?? value}</StyledFieldContent>
            ) : (
                <NoneValue>{t('commons:none')}</NoneValue>
            )}
        </Flexbox>
    )
}

interface YesNoAnswerFieldProps extends AnswerFieldProps {
    yesNoValue?: boolean
    children?: ReactNode
}
export const YesNoAnswerField: FC<YesNoAnswerFieldProps> = ({
    labelTitle,
    yesNoValue,
    hasValue = yesNoValue != undefined,
    children
}) => {
    const {t} = useTranslation()
    return (
        <AnswerField labelTitle={labelTitle} hasValue={hasValue}>
            {yesNoValue ? (
                <Flexbox gap={1} align="center">
                    <CheckIcon fill={palette.light.neutral.black} /> {t('commons:yes')}
                </Flexbox>
            ) : (
                <Flexbox gap={1} align="center">
                    <XCloseIcon fill={palette.light.neutral.black} /> {t('commons:no')}
                </Flexbox>
            )}
            {children && <StyledFieldContent>{children}</StyledFieldContent>}
        </AnswerField>
    )
}
